// Business year rollup for `#s08` (FEC-M4-02) from `GET /api/business/year/{year}`.
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const businessExpenseRowSchema = z.object({
  label: z.string(),
  amount: z.string(),
  item_count: z.number().nullable(),
  note: z.string().nullable(),
  uncategorized_count: z.number().nullable(),
});

export const businessMonthSchema = z.object({
  month: z.number().int().min(1).max(12),
  state: z.enum(['complete', 'partial', 'not_elapsed']),
  income: z.string().nullable(),
  expense: z.string().nullable(),
  expenses: z.array(businessExpenseRowSchema).default([]),
});

export const businessIncomeSourceSchema = z.object({
  label: z.string(),
  amount: z.string(),
  deposit_count: z.number().nullable(),
  account_last4: z.string().nullable(),
});

export const businessYearEventSchema = z.object({
  event_id: z.string(),
  name: z.string(),
  date_label: z.string(),
  item_count: z.number(),
  total: z.string(),
});

export const businessYearResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('unavailable') }),
  z.object({
    status: z.literal('empty'),
    tax_year: z.number(),
    period_label: z.string(),
  }),
  z.object({
    status: z.literal('insufficient'),
    tax_year: z.number(),
    period_label: z.string(),
    reason: z.enum(['mixed_currency', 'partial_source']),
  }),
  z.object({
    status: z.literal('ready'),
    tax_year: z.number(),
    period_label: z.string(),
    currency: z.string(),
    income: z.string(),
    expense: z.string(),
    net: z.string(),
    prior_year_net: z.string().nullable(),
    average_per_month: z.string().nullable(),
    average_month_count: z.number().nullable(),
    months: z.array(businessMonthSchema),
    expenses: z.array(businessExpenseRowSchema),
    income_sources: z.array(businessIncomeSourceSchema),
    events: z.array(businessYearEventSchema),
  }),
]);
export type BusinessYearResult = z.infer<typeof businessYearResultSchema>;

export function useBusinessYear(taxYear: number | undefined, timezone?: string) {
  return useQuery<BusinessYearResult>({
    queryKey: ['business-year', taxYear, timezone],
    enabled: taxYear != null && Boolean(timezone),
    queryFn: async () => {
      const response = await client.get(`/api/business/year/${taxYear}`);
      return parseEnvelope(businessYearResultSchema, response.data).data;
    },
  });
}
